import React, { useEffect, useState } from 'react';
import './AddForm.css';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_CONFIG, getApiV1Url, getApiUrl } from '../config/api';

export default function LabDataFormPage() {
    type Region = {
        id: number;
        name: string;
        state: string;
    };

    type CurrentItem = {
        id: number;
        exam_date: string;
        region_id: number;
        manufacture_name: string;
        object_name: string;
        object_adres: string;
        customer: string;
        compliance_requirements: string;
        product_name: string;
        exam_result: string;
    };

    type CurrentForm = {
        exam_date: string;
        object_name: string;
        geomaps: string;
        object_adres: string;
        reason_request: string;
        exploitation_time: string;
        customer: string;
        product_name: string;
        technical_documentation: string;
        compliance_requirements: string;
        exam_result: string;
        manufacture: number | null;
    };

    type UniqueItem = {
        id: number;
        name: string;
        inn: string;
        region: number | null;
        region_display: string | null;
        address: string;
        [key: string]: string | number | null | undefined;
    };

    type LabExamDate = {
        id?: number;
        date_updated: string;
    };

    const [currentItems, setCurrentItems] = useState<CurrentItem[]>([]);
    const [uniqueItems, setUniqueItems] = useState<UniqueItem[]>([]);
    const [regions, setRegions] = useState<Region[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [activeForm, setActiveForm] = useState<'date' | 'manufacturer' | 'detail'>('date');

    const [formCurrent, setFormCurrent] = useState<CurrentForm>({
        exam_date: '',
        object_name: '',
        geomaps: '',
        object_adres: '',
        reason_request: '',
        exploitation_time: '',
        customer: '',
        product_name: '',
        technical_documentation: '',
        compliance_requirements: '',
        exam_result: '',
        manufacture: null,
    });

    const [manufacturerInput, setManufacturerInput] = useState('');

    // Табличная строка формы добавления испытаний
    type TableRow = {
        // Поля для сопоставления производителя и региона (в базу не отправляются)
        fed_district: string;
        region_display: string; // формат: "Название | Округ"
        manufacturer_address: string;
        manufacturer_name: string;
        manufacturer_inn: string;

        // Основные поля записи
        exam_date: string;
        object_name: string;
        geomaps: string;
        object_adres: string;
        reason_request: string;
        exploitation_time: string;
        customer: string;
        product_name: string;
        technical_documentation: string;
        compliance_requirements: string;
        exam_result: string;

        // Внутренние поля маппинга производителя
        manufacture: number | null;
        manufacture_display: string;
    };

    // Пустая строка по умолчанию
    const emptyRow: TableRow = {
        fed_district: '',
        region_display: '',
        manufacturer_address: '',
        manufacturer_name: '',
        manufacturer_inn: '',
        exam_date: '',
        object_name: '',
        geomaps: '',
        object_adres: '',
        reason_request: '',
        exploitation_time: '',
        customer: '',
        product_name: '',
        technical_documentation: '',
        compliance_requirements: '',
        exam_result: '',
        manufacture: null,
        manufacture_display: ''
    };

    const [rows, setRows] = useState<TableRow[]>([emptyRow]);

    const [formUnique, setFormUnique] = useState<UniqueItem>({
        id: 0,
        name: '',
        inn: '',
        region: null,
        region_display: null,
        address: ''
    });

    const [formLabExamDate, setFormLabExamDate] = useState<LabExamDate>({
        date_updated: ''
    });

    const navigate = useNavigate();

    useEffect(() => {
        const fetchData = async () => {
            try {
                const access = localStorage.getItem('access');
                const [examsResponse, companiesResponse, regionsResponse] = await Promise.all([
                    axios.get(getApiV1Url('LAB_EXAMS_LIST'), {
                        headers: access ? { Authorization: `Bearer ${access}` } : {}
                    }),
                    axios.get(getApiV1Url('COMPANIES'), {
                        headers: access ? { Authorization: `Bearer ${access}` } : {}
                    }),
                    axios.get(getApiV1Url('REGIONS'), {
                        headers: access ? { Authorization: `Bearer ${access}` } : {}
                    })
                ]);

                setCurrentItems(examsResponse.data);
                setUniqueItems(companiesResponse.data.map((company: any) => ({
                    id: company.id,
                    name: company.name,
                    inn: company.inn,
                    region: company.region_id,
                    region_display: getRegionDisplay(company.region_id),
                    address: company.address
                })));
                setRegions(regionsResponse.data);
            } catch (error) {
                console.error('Ошибка загрузки данных:', error);
                setError('Ошибка загрузки данных');
            }
        };

        fetchData();
    }, []);

    const getRegionDisplay = (regionId: number | null): string | null => {
        if (!regionId) return null;
        const region = regions.find(r => r.id === regionId);
        return region ? `${region.name} | ${region.state}` : null;
    };

    const handleCurrentChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormCurrent(prev => ({
            ...prev,
            [name]: name === 'manufacture' ? (value ? Number(value) : null) : value,
        }));
    };

    const handleUniqueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormUnique(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleLabExamDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormLabExamDate(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const sanitizeNewlines = (rawValue: string): string => {
        if (!rawValue) {
            return '';
        }

        // Заменяем реальные переносы строк и последовательности "\n" / "/n" на пробел
        const withoutRealNewlines = rawValue.replace(/(\r\n|\n|\r)/g, ' ');
        const withoutEscapedNewlines = withoutRealNewlines
            .replace(/\\n/g, ' ')
            .replace(/\/n/g, ' ');

        return withoutEscapedNewlines;
    };

    const normalizeText = (value: unknown): string => {
        if (value === null || value === undefined) {
            return '';
        }

        const rawString = String(value);
        const sanitized = sanitizeNewlines(rawString);

        return sanitized.trim().replace(/\s+/g, ' ');
    };

    const defaultIfEmpty = (value: unknown): string => {
        if (value === null || value === undefined) {
            return 'нет данных';
        }

        const rawString = String(value);
        const sanitized = sanitizeNewlines(rawString);
        const text = sanitized.trim();

        return text ? text : 'нет данных';
    };

    const adresOptions = Array.from(new Set(currentItems.map(i => normalizeText(i.object_adres)).filter(Boolean)));
    const objectNameOptions = Array.from(new Set(currentItems.map(i => normalizeText(i.object_name)).filter(Boolean)));
    const customerOptions = Array.from(new Set(currentItems.map(i => normalizeText(i.customer)).filter(Boolean)));
    const complianceOptions = Array.from(new Set(currentItems.map(i => normalizeText(i.compliance_requirements)).filter(Boolean)));
    const examResultOptions = Array.from(new Set(currentItems.map(i => normalizeText(i.exam_result)).filter(Boolean)));
    const companyAddressOptions = Array.from(new Set(uniqueItems.map(i => i.address).filter(Boolean)));

    const mapManufacturer = (name: string, inn: string, display: string, address?: string): { id: number | null; display: string } => {
        const nameTrim = (name || '').trim();
        const innTrim = (inn || '').trim();
        const dispTrim = (display || '').trim();

        if (dispTrim) {
            let found = uniqueItems.find(c => `${c.name || 'Без названия'}${c.inn ? ' | ' + c.inn : ''}` === dispTrim);
            if (found) return { id: found.id, display: `${found.name || 'Без названия'}${found.inn ? ' | ' + found.inn : ''}` };
        }
        if (innTrim) {
            const found = uniqueItems.find(c => c.inn && c.inn === innTrim);
            if (found) return { id: found.id, display: `${found.name || 'Без названия'}${found.inn ? ' | ' + found.inn : ''}` };
        }
        if (nameTrim) {
            const lower = nameTrim.toLowerCase();
            const found = uniqueItems.find(c => (c.name || '').toLowerCase() === lower);
            if (found) return { id: found.id, display: `${found.name || 'Без названия'}${found.inn ? ' | ' + found.inn : ''}` };
        }
        if (address && address.trim()) {
            const addr = address.trim().toLowerCase();
            const found = uniqueItems.find(c => (c.address || '').toLowerCase() === addr);
            if (found) return { id: found.id, display: `${found.name || 'Без названия'}${found.inn ? ' | ' + found.inn : ''}` };
        }
        const label = nameTrim || dispTrim || '';
        return { id: null, display: label };
    };

    // Пересчет ID производителя при изменении полей строки
    const recomputeManufacturer = (row: TableRow): { manufacture: number | null; manufacture_display: string } => {
        const res = mapManufacturer(
            row.manufacturer_name,
            row.manufacturer_inn,
            row.manufacture_display,
            row.manufacturer_address
        );
        return { manufacture: res.id, manufacture_display: res.display };
    };

    // изменения в ячейке таблицы
    const handleCellChange = (rowIndex: number, field: keyof TableRow, value: string) => {
        setRows(prev => {
            const next = [...prev];
            const row = { ...next[rowIndex] };
            if (field === 'manufacture_display') {
                row.manufacture_display = value;
                const mapped = recomputeManufacturer(row);
                row.manufacture = mapped.manufacture;
                row.manufacture_display = mapped.manufacture_display;
            } else if (field === 'manufacturer_name' || field === 'manufacturer_inn' || field === 'manufacturer_address') {
                // Пересчитываем сопоставление при изменении составляющих
                // @ts-ignore
                row[field] = value;
                const mapped = recomputeManufacturer(row);
                row.manufacture = mapped.manufacture;
                row.manufacture_display = mapped.manufacture_display;
            } else if (field === 'manufacture') {
                row.manufacture = value ? Number(value) : null;
            } else {
                // @ts-ignore safe for string fields
                row[field] = value;
            }
            next[rowIndex] = row;
            return next;
        });
    };

    // Один ряд: замена на пустой
    const addEmptyRow = () => setRows([ { ...emptyRow } ]);
    const removeRow = (rowIndex: number) => setRows([ { ...emptyRow } ]);

    const collapseNewlinesInQuotedCells = (rawText: string): string => {
        let result = '';
        let inQuotes = false;
        let i = 0;

        while (i < rawText.length) {
            const char = rawText[i];

            if (char === '"') {
                if (inQuotes && i + 1 < rawText.length && rawText[i + 1] === '"') {
                    result += '"';
                    i += 2;
                    continue;
                }
                inQuotes = !inQuotes;
                result += char;
                i += 1;
                continue;
            }

            if (char === '\n' && inQuotes) {
                result += ' ';
                i += 1;
                continue;
            }

            result += char;
            i += 1;
        }

        return result;
    };

    // вставки из excel/google таблицы 
    const parsePastedTable = (text: string): TableRow[] => {
        const withoutCarriageReturn = text.replace(/\r/g, '');
        const cleanedText = collapseNewlinesInQuotedCells(withoutCarriageReturn);

        const lines = cleanedText.split('\n').filter(l => l.trim() !== '');
        if (!lines.length) return [];
        return lines.map(line => {
            const cells = line.includes('\t') ? line.split('\t') : line.split(',');
            // Ожидаемый порядок колонок:
            // 0: фед округ, 1: область, 2: адрес (производ.), 3: наименование производителя, 4: инн,
            // 5: дата фиксации, 6: наименование объекта, 7: геометка, 8: адрес объекта, 9: причина обращения,
            // 10: срок эксплуатации, 11: заказчик, 12: наименование продукции, 13: сведения о НД,
            // 14: соответствие требованиям, 15: результат
            const get = (i: number) => (cells[i] ?? '').toString().trim();
            const fed_district = get(0);
            const region_display = get(1);
            const manufacturer_address = get(2);
            const manufacturer_name = get(3);
            const manufacturer_inn = get(4);
            const exam_date = get(5);
            const object_name = get(6);
            const geomaps = get(7);
            const object_adres = get(8);
            const reason_request = get(9);
            const exploitation_time = get(10);
            const customer = get(11);
            const product_name = get(12);
            const technical_documentation = get(13);
            const compliance_requirements = get(14);
            const exam_result = get(15);

            const mapped = mapManufacturer(manufacturer_name, manufacturer_inn, `${manufacturer_name}${manufacturer_inn ? ' | ' + manufacturer_inn : ''}`, manufacturer_address);
            return {
                fed_district,
                region_display,
                manufacturer_address,
                manufacturer_name,
                manufacturer_inn,
                exam_date,
                object_name,
                geomaps,
                object_adres,
                reason_request,
                exploitation_time,
                customer,
                product_name,
                technical_documentation,
                compliance_requirements,
                exam_result,
                manufacture: mapped.id,
                manufacture_display: mapped.display
            } as TableRow;
        });
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
        const text = e.clipboardData.getData('text');
        if (!text) return;
        e.preventDefault();
        const parsed = parsePastedTable(text);
        if (!parsed.length) return;
        setRows([ parsed[0] ]);
    };

    // отправка всех строк разом
    const bulkSubmit = async () => {
        try {
            const access = localStorage.getItem('access');
            const headers = {
                'Content-Type': 'application/json',
                ...(access ? { Authorization: `Bearer ${access}` } : {})
            } as any;

            const payloads = rows.map(r => ({
                exam_date: r.exam_date || null,
                object_name: defaultIfEmpty(r.object_name),
                geomaps: defaultIfEmpty(r.geomaps),
                object_adres: defaultIfEmpty(r.object_adres),
                reason_request: defaultIfEmpty(r.reason_request),
                exploitation_time: defaultIfEmpty(r.exploitation_time),
                customer: defaultIfEmpty(r.customer),
                product_name: defaultIfEmpty(r.product_name),
                technical_documentation: defaultIfEmpty(r.technical_documentation),
                compliance_requirements: defaultIfEmpty(r.compliance_requirements),
                exam_result: defaultIfEmpty(r.exam_result),
                manufacture: r.manufacture
            }));

            const results = await Promise.allSettled(
                payloads.map(p => axios.post(getApiV1Url('LAB_EXAMS'), p, { headers }))
            );

            const successCount = results.filter(r => r.status === 'fulfilled').length;
            const failCount = results.length - successCount;
            if (failCount > 0) {
                setSuccessMessage(null);
                setError(`Успешно добавлено: ${successCount}. Ошибки: ${failCount}.`);
            } else {
                setError(null);
                setSuccessMessage(`Успешно добавлено: ${successCount}. Ошибки: ${failCount}.`);
                setTimeout(() => setSuccessMessage(null), 4000);
                setRows([ { ...emptyRow } ]);
            }
        } catch (err) {
            setError('Ошибка при массовом добавлении записей');
            console.error(err);
        }
    };

    const addCurrentItem = async () => {
        try {
            const access = localStorage.getItem('access');
            const newItem = {
                exam_date: formCurrent.exam_date || null,
                object_name: defaultIfEmpty(formCurrent.object_name),
                geomaps: defaultIfEmpty(formCurrent.geomaps),
                object_adres: defaultIfEmpty(formCurrent.object_adres),
                reason_request: defaultIfEmpty(formCurrent.reason_request),
                exploitation_time: defaultIfEmpty(formCurrent.exploitation_time),
                customer: defaultIfEmpty(formCurrent.customer),
                product_name: defaultIfEmpty(formCurrent.product_name),
                technical_documentation: defaultIfEmpty(formCurrent.technical_documentation),
                compliance_requirements: defaultIfEmpty(formCurrent.compliance_requirements),
                exam_result: defaultIfEmpty(formCurrent.exam_result),
                manufacture: formCurrent.manufacture,
            };

            console.log('Отправляемые данные:', newItem);

            await axios.post(getApiV1Url('LAB_EXAMS'), newItem, {
                headers: {
                    'Content-Type': 'application/json',
                    ...(access ? { Authorization: `Bearer ${access}` } : {})
                }
            });

            setFormCurrent({
                exam_date: '',
                object_name: '',
                geomaps: '',
                object_adres: '',
                reason_request: '',
                exploitation_time: '',
                customer: '',
                product_name: '',
                technical_documentation: '',
                compliance_requirements: '',
                exam_result: '',
                manufacture: null,
            });
            setManufacturerInput('');
        } catch (err) {
            setError('Ошибка при добавлении записи');
            console.error('Ошибка при добавлении записи:', err);
        }
    };

    const addUniqueItem = async () => {
        try {
            const access = localStorage.getItem('access');

            const innTrimmed = String(formUnique.inn || '').trim();
            const innToSend = innTrimmed ? innTrimmed : 'нет данных';

           

            const dataToSend = {
                name: defaultIfEmpty(formUnique.name),
                inn: innToSend,
                region: formUnique.region,
                address: defaultIfEmpty(formUnique.address)
            };

            console.log('Отправляемые данные:', dataToSend);

            const response = await axios.post(getApiV1Url('COMPANY_CREATE'), dataToSend, {
                headers: {
                    'Content-Type': 'application/json',
                    ...(access ? { Authorization: `Bearer ${access}` } : {})
                }
            });

            console.log('Ответ сервера:', response.data);

            setFormUnique({
                id: 0,
                name: '',
                inn: '',
                region: null,
                region_display: null,
                address: ''
            });
            setError(null);
        } catch (err) {
            if (axios.isAxiosError(err)) {
                const errorMessage = err.response?.data?.detail ||
                    err.response?.data?.message ||
                    JSON.stringify(err.response?.data) ||
                    err.message;
                setError(`Ошибка сервера: ${errorMessage}`);
            } else {
                setError('Неизвестная ошибка при добавлении производителя');
            }
            console.error('Ошибка при добавлении производителя:', err);
        }
    };

    const addLabExamDate = async () => {
        try {
            const access = localStorage.getItem('access');
            if (!formLabExamDate.date_updated) {
                setError('Заполните обязательное поле: Дата обновления');
                return;
            }

            const dataToSend = {
                date_updated: formLabExamDate.date_updated
            };


            const response = await axios.post(getApiV1Url('DATE') + '/update/', dataToSend, {
                headers: {
                    'Content-Type': 'application/json',
                    ...(access ? { Authorization: `Bearer ${access}` } : {})
                }
            });

            console.log('Ответ сервера для даты:', response.data);

            setFormLabExamDate({
                date_updated: ''
            });
            setSuccessMessage('Дата успешно добавлена');
            setError(null);

            setTimeout(() => setSuccessMessage(null), 3000);
        } catch (err) {
            if (axios.isAxiosError(err)) {
                console.log('Детали ошибки:', err.response?.data);
                console.log('Статус:', err.response?.status);
                console.log('Заголовки:', err.response?.headers);
                
                const errorMessage = err.response?.data?.detail ||
                    err.response?.data?.message ||
                    err.response?.data?.error ||
                    JSON.stringify(err.response?.data) ||
                    err.message;
                setError(`Ошибка сервера (${err.response?.status}): ${errorMessage}`);
            } else {
                setError('Неизвестная ошибка при добавлении даты');
            }
            console.error('Ошибка при добавлении даты:', err);
        }
    };

    const DateUpdateForm = () => (
        <div className="form-container">
            <h2>Добавить дату лабораторных испытаний</h2>
            <div className="form-group">
                <label>Дата обновления</label>
                <input
                    type="date"
                    name="date_updated"
                    value={formLabExamDate.date_updated}
                    onChange={handleLabExamDateChange}
                    required
                />
            </div>
            <button className="submit-button" onClick={addLabExamDate}>
                Добавить дату
            </button>
        </div>
    );

    const ManufacturerForm = () => (
        <div className="form-container">
            <h2>Добавить производителя</h2>
            <div className="form-group">
                <label>Наименование производителя</label>
                <input
                    type="text"
                    name="name"
                    value={String(formUnique.name || '')}
                    onChange={handleUniqueChange}
                />
            </div>
            <div className="form-group">
                <label>ИНН производителя (если компания — можно оставить пустым)</label>
                <input
                    type="text"
                    name="inn"
                    value={String(formUnique.inn || '')}
                    onChange={handleUniqueChange}
                />
            </div>
            <div className="form-group">
                <label>Область</label>
                <input
                    type="text"
                    list="region-list-unique"
                    value={String(formUnique.region_display || '')}
                    onChange={(e) => {
                        const inputValue = e.target.value;
                        //При вводе ищет регион,если есть название ставит ID, есои нет,то NULL
                        const region = regions.find(r => `${r.name} | ${r.state}` === inputValue);
                        if (region) {
                            setFormUnique(prev => ({ ...prev, region: region.id, region_display: `${region.name} | ${region.state}` }));
                        } else {
                            setFormUnique(prev => ({ ...prev, region: null, region_display: inputValue }));
                        }
                    }}
                    placeholder="Введите область или выберите из списка"
                />
                <datalist id="region-list-unique">
                    {regions.map((region) => (
                        <option key={region.id} value={`${region.name} | ${region.state}`}>
                            {region.name} | {region.state}
                        </option>
                    ))}
                </datalist>
            </div>
            <div className="form-group">
                <label>Адрес производителя</label>
                <input
                    list="company-address-list"
                    type="text"
                    name="address"
                    value={String(formUnique.address || '')}
                    onChange={handleUniqueChange}
                />
                <datalist id="company-address-list">
                    {companyAddressOptions.map((opt, i) => (
                        <option key={i} value={String(opt)} />
                    ))}
                </datalist>
            </div>
            <button className="submit-button" onClick={addUniqueItem}>
                Добавить производителя
            </button>
        </div>
    );

    const DetailRecordForm = () => (
        <div className="form-container">
            <h2>Добавить записи (таблица + вставка из Excel)</h2>
            <div className="form-group">
                <label>Вставьте строки (TSV/Excel): порядок колонок</label>
                <div className="table-help">
                    exam_date, object_name, geomaps, object_adres, reason_request, exploitation_time, customer, product_name, technical_documentation, compliance_requirements, exam_result, manufacturer ("name | inn" или inn или name)
                </div>
                <textarea onPaste={handlePaste} placeholder="Скопируйте строки из Excel и вставьте сюда" />
            </div>

            <div className="table-wrapper">
                <table className="table-form">
                    <thead>
                        <tr>
                            <th>Фед. округ</th>
                            <th>Область</th>
                            <th>Адрес (производ.)</th>
                            <th>Наименование производителя</th>
                            <th>ИНН</th>
                            <th>Дата фиксации</th>
                            <th>Наименование объекта</th>
                            <th>Геометка</th>
                            <th>Адрес объекта</th>
                            <th>Причина обращения</th>
                            <th>Срок эксплуатации</th>
                            <th>Заказчик</th>
                            <th>Наименование продукции</th>
                            <th>Сведения о НД</th>
                            <th>Соответствие требованиям</th>
                            <th>Результат</th>
                            <th>Производитель (подсказка)</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r, idx) => (
                            <tr key={idx}>
                                <td>
                                    <input list="fed-district-list" value={r.fed_district} onChange={(e) => handleCellChange(idx, 'fed_district', e.target.value)} />
                                </td>
                                <td>
                                    <input list="region-list-unique" value={r.region_display} onChange={(e) => handleCellChange(idx, 'region_display', e.target.value)} />
                                </td>
                                <td>
                                    <input value={r.manufacturer_address} onChange={(e) => handleCellChange(idx, 'manufacturer_address', e.target.value)} />
                                </td>
                                <td>
                                    <input value={r.manufacturer_name} onChange={(e) => handleCellChange(idx, 'manufacturer_name', e.target.value)} />
                                </td>
                                <td>
                                    <input value={r.manufacturer_inn} onChange={(e) => handleCellChange(idx, 'manufacturer_inn', e.target.value)} />
                                </td>
                                <td>
                                    <input type="date" value={r.exam_date} onChange={(e) => handleCellChange(idx, 'exam_date', e.target.value)} />
                                </td>
                                <td>
                                    <input list="object-name-list" value={r.object_name} onChange={(e) => handleCellChange(idx, 'object_name', e.target.value)} />
                                </td>
                                <td>
                                    <input value={r.geomaps} onChange={(e) => handleCellChange(idx, 'geomaps', e.target.value)} />
                                </td>
                                <td>
                                    <input list="adres-list" value={r.object_adres} onChange={(e) => handleCellChange(idx, 'object_adres', e.target.value)} />
                                </td>
                                <td>
                                    <input value={r.reason_request} onChange={(e) => handleCellChange(idx, 'reason_request', e.target.value)} />
                                </td>
                                <td>
                                    <input value={r.exploitation_time} onChange={(e) => handleCellChange(idx, 'exploitation_time', e.target.value)} />
                                </td>
                                <td>
                                    <input list="customer-list" value={r.customer} onChange={(e) => handleCellChange(idx, 'customer', e.target.value)} />
                                </td>
                                <td>
                                    <input value={r.product_name} onChange={(e) => handleCellChange(idx, 'product_name', e.target.value)} />
                                </td>
                                <td>
                                    <input value={r.technical_documentation} onChange={(e) => handleCellChange(idx, 'technical_documentation', e.target.value)} />
                                </td>
                                <td>
                                    <input list="compliance-list" value={r.compliance_requirements} onChange={(e) => handleCellChange(idx, 'compliance_requirements', e.target.value)} />
                                </td>
                                <td>
                                    <input list="exam-result-list" value={r.exam_result} onChange={(e) => handleCellChange(idx, 'exam_result', e.target.value)} />
                                </td>
                                <td>
                                    <input list="manufacturer-list" placeholder="Название | ИНН" value={r.manufacture_display} onChange={(e) => handleCellChange(idx, 'manufacture_display', e.target.value)} />
                                </td>
                                <td>
                                    <button className="btn-danger" onClick={() => removeRow(idx)}>Очистить</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="table-actions">
                <button className="btn-secondary" onClick={addEmptyRow}>Очистить</button>
                <button className="submit-button" onClick={bulkSubmit}>Сохранить</button>
            </div>

            {/* Datalists for hints */}
            <datalist id="object-name-list">
                {objectNameOptions.map((opt, i) => (
                    <option key={i} value={String(opt)} />
                ))}
            </datalist>
            <datalist id="adres-list">
                {adresOptions.map((opt, i) => (
                    <option key={i} value={String(opt)} />
                ))}
            </datalist>
            <datalist id="customer-list">
                {customerOptions.map((opt, i) => (
                    <option key={i} value={String(opt)} />
                ))}
            </datalist>
            <datalist id="compliance-list">
                {complianceOptions.map((opt, i) => (
                    <option key={i} value={String(opt)} />
                ))}
            </datalist>
            <datalist id="exam-result-list">
                {examResultOptions.map((opt, i) => (
                    <option key={i} value={String(opt)} />
                ))}
            </datalist>
            <datalist id="manufacturer-list">
                {uniqueItems
                    .sort((a, b) => (a.name || 'Без названия').localeCompare(b.name || 'Без названия'))
                    .map((c) => (
                        <option key={c.id} value={`${c.name || 'Без названия'}${c.inn ? ' | ' + c.inn : ''}`} />
                    ))
                }
            </datalist>
            <datalist id="fed-district-list">
                {Array.from(new Set(regions.map(r => r.state))).map((state, i) => (
                    <option key={i} value={String(state)} />
                ))}
            </datalist>
            <datalist id="region-list-unique">
                {regions.map((region) => (
                    <option key={region.id} value={`${region.name} | ${region.state}`} />
                ))}
            </datalist>

            <div className="table-help" style={{ marginTop: 8 }}>
                Поля "Фед. округ", "Область", "Адрес (производ.)", "Наименование производителя", "ИНН" используются для сопоставления производителя и подсказок.
                Если требуется сохранять их в базе, напишите — расширим API.
            </div>
        </div>
    );

    return (
        <div className="page-container">
            <nav className="breadcrumbs">
                <span
                    className="breadcrumb-home"
                    style={{ color: 'rgba(0,0,0,0.5)', cursor: 'pointer', fontWeight: 400 }}
                    onClick={() => navigate('/')}
                >
                    Главная
                </span>
                <span style={{ margin: '0 8px', color: 'rgba(0,0,0,0.3)' }}>/</span>
                <span className="breadcrumb-current" style={{ fontWeight: 500, color: '#000', }}>
                    Добавление записей
                </span>
            </nav>

            {error && <div className="error-message">{error}</div>}
            {successMessage && <div className="success-message">{successMessage}</div>}

            <div className="form-tabs">
                <button className={`form-tab ${activeForm === 'date' ? 'active' : ''}`} onClick={() => setActiveForm('date')}>Дата</button>
                <button className={`form-tab ${activeForm === 'detail' ? 'active' : ''}`} onClick={() => setActiveForm('detail')}>Испытания</button>
                <button className={`form-tab ${activeForm === 'manufacturer' ? 'active' : ''}`} onClick={() => setActiveForm('manufacturer')}>Продавец</button>
            </div>

            {activeForm === 'date' && DateUpdateForm()}

            {activeForm === 'manufacturer' && ManufacturerForm()}

            {activeForm === 'detail' && DetailRecordForm()}
        </div>
    );
}